import { useEffect, useState, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { GoingHomeAlertGrid } from "@/components/GoingHomeAlertGrid";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { DogIcon, Clock, User, Home, MapPin, MessageSquare, Thermometer, Volume2, AlertTriangle, Weight, Calendar } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ZIP code to coordinates mapping for weather
const ZIP_COORDINATES = {
  '18914': { lat: 40.29, lon: -75.21, name: 'Chalfont' },
  '19464': { lat: 40.13, lon: -75.52, name: 'Pottstown' },
  '08540': { lat: 40.36, lon: -74.66, name: 'Princeton' },
};
const DEFAULT_COORDS = { lat: 40.29, lon: -75.21 };

const AUTO_REFRESH_INTERVAL = 60 * 60 * 1000;
const ALERT_MS = 45000;
const MAX_ALERTS = 4;
// One alert per owner + dogs; custom messages are keyed by their text.
const alertKey = (n) => n.type === "custom_message"
  ? `msg:${n.message}`
  : `${n.owner_id || n.owner_name || ""}|${(n.animals?.length ? n.animals : [n]).map(a => a.animal_id || a.animal_name).sort().join(",")}`;

// ─── Dog-Friendly Notification Sound ─────────────────────────────────
const playDogFriendlyNotificationSound = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const notes = [
      { freq: 523.25, start: 0, duration: 0.4 },
      { freq: 659.25, start: 0.35, duration: 0.4 },
      { freq: 783.99, start: 0.7, duration: 0.5 },
      { freq: 1046.50, start: 1.1, duration: 0.7 },
    ];
    const createWarmPad = (startTime) => {
      const osc = audioContext.createOscillator();
      const gain = audioContext.createGain();
      const filter = audioContext.createBiquadFilter();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(261.63, audioContext.currentTime);
      filter.type = 'lowpass';
      filter.frequency.setValueAtTime(1500, audioContext.currentTime);
      filter.Q.setValueAtTime(1, audioContext.currentTime);
      gain.gain.setValueAtTime(0, audioContext.currentTime + startTime);
      gain.gain.linearRampToValueAtTime(0.08, audioContext.currentTime + startTime + 0.3);
      gain.gain.linearRampToValueAtTime(0.06, audioContext.currentTime + startTime + 1.5);
      gain.gain.linearRampToValueAtTime(0, audioContext.currentTime + startTime + 2.5);
      osc.connect(filter); filter.connect(gain); gain.connect(audioContext.destination);
      osc.start(audioContext.currentTime + startTime);
      osc.stop(audioContext.currentTime + startTime + 2.5);
    };
    const createNote = (frequency, startTime, duration) => {
      const osc = audioContext.createOscillator();
      const gain = audioContext.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(frequency, audioContext.currentTime);
      const ns = audioContext.currentTime + startTime;
      gain.gain.setValueAtTime(0, ns);
      gain.gain.linearRampToValueAtTime(0.15, ns + 0.08);
      gain.gain.exponentialRampToValueAtTime(0.08, ns + duration * 0.5);
      gain.gain.exponentialRampToValueAtTime(0.001, ns + duration);
      osc.connect(gain); gain.connect(audioContext.destination);
      osc.start(ns); osc.stop(ns + duration);
      const h = audioContext.createOscillator();
      const hg = audioContext.createGain();
      h.type = 'sine';
      h.frequency.setValueAtTime(frequency * 2, audioContext.currentTime);
      hg.gain.setValueAtTime(0, ns);
      hg.gain.linearRampToValueAtTime(0.03, ns + 0.08);
      hg.gain.exponentialRampToValueAtTime(0.001, ns + duration * 0.7);
      h.connect(hg); hg.connect(audioContext.destination);
      h.start(ns); h.stop(ns + duration);
    };
    createWarmPad(0);
    notes.forEach(n => createNote(n.freq, n.start, n.duration));
    setTimeout(() => audioContext.close(), 3000);
  } catch (error) {
    console.error('Could not play notification sound:', error);
  }
};

// Room label helper
const getRoomLabel = (room) => {
  if (room === 'large') return 'Large Dog Room';
  if (room === 'small') return 'Small Dog Room';
  return 'All Rooms';
};

const getRoomColor = (room) => {
  if (room === 'large') return { bg: 'from-emerald-900 via-green-900 to-teal-900', accent: 'bg-emerald-500', text: 'text-emerald-400' };
  if (room === 'small') return { bg: 'from-blue-900 via-indigo-900 to-purple-900', accent: 'bg-blue-500', text: 'text-blue-400' };
  return { bg: 'from-purple-900 via-blue-900 to-indigo-900', accent: 'bg-purple-500', text: 'text-purple-400' };
};

function GoingHomeDisplay() {
  const [searchParams] = useSearchParams();
  // Up to 4 alerts on screen at once (45s each, from when shown); extras wait in the queue.
  const [alerts, setAlerts] = useState({ active: [], queue: [] });
  const alertSeq = useRef(0);
  const alertsRef = useRef(alerts);
  alertsRef.current = alerts;
  const currentNotification = alerts.active[0] || null;
  const [allNotifications, setAllNotifications] = useState([]);
  const [connectionStatus, setConnectionStatus] = useState('connected');
  const [locationName, setLocationName] = useState('');
  const [locationZip, setLocationZip] = useState('18914');
  const [currentTime, setCurrentTime] = useState(new Date());
  const [weather, setWeather] = useState(null);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [disconnectedSince, setDisconnectedSince] = useState(null);
  const [showDisconnectBanner, setShowDisconnectBanner] = useState(false);
  
  // Going home dog list for idle display
  const [goingHomeDogs, setGoingHomeDogs] = useState([]);
  // Dog spotlight rotation
  const [spotlightIndex, setSpotlightIndex] = useState(0);
  // Right panel rotation: 0 = next pickup, 1 = dog spotlight
  const [rightPanelIndex, setRightPanelIndex] = useState(0);

  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const wakeLockRef = useRef(null);

  // Get URL params
  const urlLocationParam = searchParams.get('location');
  const roomParam = searchParams.get('room'); // "large", "small", or null (all)
  const localStorageLocationId = typeof window !== 'undefined' ? localStorage.getItem('currentLocationId') : null;
  const locationId = urlLocationParam || localStorageLocationId || 'default';

  const roomColors = getRoomColor(roomParam);

  // ─── Auto-refresh if disconnected > 10 min ──────────────────────────
  useEffect(() => {
    if (connectionStatus !== 'connected') {
      if (!disconnectedSince) setDisconnectedSince(new Date());
      else if (new Date() - disconnectedSince > 600000) window.location.reload();
    } else {
      setDisconnectedSince(null);
    }
  }, [connectionStatus, disconnectedSince]);

  useEffect(() => {
    const iv = setInterval(() => {
      if (disconnectedSince && new Date() - disconnectedSince > 600000) window.location.reload();
    }, 60000);
    return () => clearInterval(iv);
  }, [disconnectedSince]);

  // ─── Fetch location name + zip ────────────────────────────────────────
  useEffect(() => {
    const fetchLocationData = async () => {
      try {
        if (locationId !== 'default') {
          const apiBase = window.location.origin;
          const response = await fetch(`${apiBase}/api/locations/${locationId}/public`);
          if (response.ok) {
            const data = await response.json();
            setLocationName(data.name || '');
            if (data.zip_code) setLocationZip(data.zip_code);
          }
        }
      } catch (e) { /* ignore */ }
    };
    fetchLocationData();
  }, [locationId]);

  // ─── Auto-refresh every 60 min ─────────────────────────────────────
  useEffect(() => {
    const iv = setInterval(() => window.location.reload(), AUTO_REFRESH_INTERVAL);
    return () => clearInterval(iv);
  }, []);

  // ─── Wake Lock ──────────────────────────────────────────────────────
  useEffect(() => {
    const requestWakeLock = async () => {
      try {
        if ('wakeLock' in navigator) {
          wakeLockRef.current = await navigator.wakeLock.request('screen');
          wakeLockRef.current.addEventListener('release', () => {});
        }
      } catch (err) { /* ignore */ }
    };
    requestWakeLock();
    const handleVis = () => { if (document.visibilityState === 'visible') requestWakeLock(); };
    document.addEventListener('visibilitychange', handleVis);
    return () => {
      document.removeEventListener('visibilitychange', handleVis);
      if (wakeLockRef.current) wakeLockRef.current.release();
    };
  }, []);

  // ─── Clock ──────────────────────────────────────────────────────────
  useEffect(() => {
    const iv = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(iv);
  }, []);

  // ─── Weather ────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchWeather = async () => {
      try {
        const coords = ZIP_COORDINATES[locationZip] || DEFAULT_COORDS;
        const resp = await fetch(`https://api.open-meteo.com/v1/forecast?latitude=${coords.lat}&longitude=${coords.lon}&current=temperature_2m,weather_code&temperature_unit=fahrenheit&timezone=America%2FNew_York`);
        if (resp.ok) {
          const data = await resp.json();
          setWeather({ temperature: Math.round(data.current.temperature_2m), weatherCode: data.current.weather_code });
        }
      } catch (e) { /* ignore */ }
    };
    fetchWeather();
    const iv = setInterval(fetchWeather, 600000);
    return () => clearInterval(iv);
  }, [locationZip]);

  const getWeatherDescription = (code) => {
    if (code === 0) return 'Clear';
    if (code <= 3) return 'Cloudy';
    if (code >= 45 && code <= 48) return 'Foggy';
    if (code >= 51 && code <= 55) return 'Drizzle';
    if (code >= 61 && code <= 65) return 'Rain';
    if (code >= 71 && code <= 77) return 'Snow';
    if (code >= 80 && code <= 82) return 'Showers';
    if (code >= 95 && code <= 99) return 'Thunderstorm';
    return '';
  };

  // ─── Fetch Going Home dogs list for idle display ─────────────────────
  const fetchGoingHomeDogs = useCallback(async () => {
    try {
      const apiBase = window.location.origin;
      // Use the public going-home cache endpoint (no auth needed for TV)
      const resp = await fetch(`${apiBase}/api/going-home/tv-dogs/${locationId}`);
      if (resp.ok) {
        const data = await resp.json();
        let dogs = data.animals || [];
        // The Going Home / pickup board is DAYCARE-ONLY — never show Private Play
        // or Day Boarding dogs (they have their own handling on the web board).
        dogs = dogs.filter(d => !d.dog_category || d.dog_category === 'daycare');
        // Filter by room if room param is set
        if (roomParam) {
          dogs = dogs.filter(d => d.room === roomParam);
        }
        setGoingHomeDogs(dogs);
      }
    } catch (e) { /* ignore */ }
  }, [locationId, roomParam]);

  useEffect(() => {
    fetchGoingHomeDogs();
    const iv = setInterval(fetchGoingHomeDogs, 30000); // Refresh every 30s
    return () => clearInterval(iv);
  }, [fetchGoingHomeDogs]);

  // ─── Right panel rotation (every 15s) ────────────────────────────────
  useEffect(() => {
    if (currentNotification) return; // Don't rotate during alert
    const iv = setInterval(() => {
      setRightPanelIndex(prev => (prev + 1) % 2);
      setSpotlightIndex(prev => {
        if (goingHomeDogs.length === 0) return 0;
        return (prev + 1) % goingHomeDogs.length;
      });
    }, 15000);
    return () => clearInterval(iv);
  }, [currentNotification, goingHomeDogs.length]);

  // ─── NOTIFICATION PROCESSING ──────────────────────────────────────────
  const reconnectAttemptRef = useRef(0);
  const cleaningUpRef = useRef(false);
  const connectingRef = useRef(false);
  const soundEnabledRef = useRef(soundEnabled);
  const seenNotificationIds = useRef(new Set());
  const lastPollTimeRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const pollFailCount = useRef(0);

  useEffect(() => { soundEnabledRef.current = soundEnabled; }, [soundEnabled]);

  // Check if a notification matches this TV's room filter
  const notificationMatchesRoom = useCallback((data) => {
    if (!roomParam) return true; // No filter = show all
    
    // Check the notification's rooms array
    if (data.rooms && Array.isArray(data.rooms)) {
      // Empty rooms array = send to all TVs (e.g. "both" target)
      if (data.rooms.length === 0) return true;
      return data.rooms.includes(roomParam);
    }
    // Check individual animals for room
    if (data.animals && Array.isArray(data.animals)) {
      return data.animals.some(a => a.room === roomParam);
    }
    // Single animal legacy format
    if (data.room) return data.room === roomParam;
    // No room info = show on all TVs (backwards compat)
    return true;
  }, [roomParam]);

  const processNotification = useCallback((data) => {
    const notifId = data.notification_id || data.animal_id || `${data.owner_name}_${data.animal_name}_${data._created_at || data.timestamp || ''}`;
    if (seenNotificationIds.current.has(notifId)) return;
    seenNotificationIds.current.add(notifId);
    if (seenNotificationIds.current.size > 100) {
      const arr = [...seenNotificationIds.current];
      seenNotificationIds.current = new Set(arr.slice(-50));
    }

    // Room filter: only show alerts for this TV's room
    if (!notificationMatchesRoom(data)) return;

    const key = alertKey(data);
    const cur = alertsRef.current;
    if ([...cur.active, ...cur.queue].some(a => a._key === key)) return;
    alertsRef.current = { ...cur, queue: [...cur.queue, { _key: key }] };
    if (soundEnabledRef.current) playDogFriendlyNotificationSound();
    const item = { ...data, _seq: ++alertSeq.current, _shownAt: Date.now(), _key: key };
    setAlerts(prev => {
      // Same owner + dogs already on screen or waiting → no change (repeat taps / duplicate delivery).
      if ([...prev.active, ...prev.queue].some(a => a._key === item._key)) return prev;
      return prev.active.length < MAX_ALERTS
        ? { ...prev, active: [...prev.active, item] }
        : { ...prev, queue: [...prev.queue, item] };
    });
    setAllNotifications(prev => [data, ...prev].slice(0, 10));
  }, [notificationMatchesRoom]);

  // Expire alerts after 45s on screen and promote queued ones into free spots.
  useEffect(() => {
    setAlerts(prev => {
      if (!prev.active.length && !prev.queue.length) return prev;
      const now = currentTime.getTime();
      const active = prev.active.filter(a => now - a._shownAt < ALERT_MS);
      const queue = [...prev.queue];
      while (active.length < MAX_ALERTS && queue.length) active.push({ ...queue.shift(), _shownAt: now });
      if (active.length === prev.active.length && queue.length === prev.queue.length) return prev;
      return { active, queue };
    });
  }, [currentTime]);

  // ─── HTTP POLLING — PRIMARY CONNECTION ────────────────────────────────
  useEffect(() => {
    const pollForNotifications = async () => {
      try {
        const apiBase = window.location.origin;
        const since = lastPollTimeRef.current || new Date(Date.now() - ALERT_MS).toISOString();
        const resp = await fetch(`${apiBase}/api/going-home/tv-poll/${locationId}?since=${encodeURIComponent(since)}`);
        if (resp.ok) {
          const data = await resp.json();
          pollFailCount.current = 0;
          lastPollTimeRef.current = data.server_time;
          setConnectionStatus('connected');
          if (data.notifications && data.notifications.length > 0) {
            [...data.notifications].reverse().forEach(processNotification);
          }
        } else {
          pollFailCount.current++;
        }
      } catch (e) {
        pollFailCount.current++;
      }
      if (pollFailCount.current >= 20) setConnectionStatus('disconnected');
    };
    pollForNotifications();
    pollIntervalRef.current = setInterval(pollForNotifications, 3000);
    return () => { if (pollIntervalRef.current) clearInterval(pollIntervalRef.current); };
  }, [locationId, processNotification]);

  // ─── WEBSOCKET — BONUS INSTANT DELIVERY ──────────────────────────────
  const connectWebSocketRef = useRef(null);
  connectWebSocketRef.current = () => {
    if (connectingRef.current) return;
    connectingRef.current = true;
    if (reconnectTimeoutRef.current) { clearTimeout(reconnectTimeoutRef.current); reconnectTimeoutRef.current = null; }
    if (wsRef.current) {
      cleaningUpRef.current = true;
      try { if (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING) wsRef.current.close(); } catch (e) { /* ignore */ }
      wsRef.current = null;
      cleaningUpRef.current = false;
    }
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsHost = window.location.host;
    const websocketUrl = `${wsProtocol}//${wsHost}/api/ws/going-home/${locationId}`;
    let websocket;
    try { websocket = new WebSocket(websocketUrl); } catch (e) { connectingRef.current = false; scheduleReconnect(); return; }
    const timeout = setTimeout(() => { if (websocket.readyState !== WebSocket.OPEN) websocket.close(); }, 8000);
    websocket.onopen = () => {
      clearTimeout(timeout); connectingRef.current = false; reconnectAttemptRef.current = 0;
      if (pingIntervalRef.current) clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = setInterval(() => {
        if (websocket.readyState === WebSocket.OPEN) { try { websocket.send('ping'); } catch (e) { websocket.close(); } }
      }, 10000);
    };
    websocket.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'ping' || data.type === 'connected') return;
        processNotification(data);
      } catch (e) { /* text message like "pong" */ }
    };
    websocket.onerror = () => { clearTimeout(timeout); connectingRef.current = false; };
    websocket.onclose = () => {
      clearTimeout(timeout); connectingRef.current = false;
      if (pingIntervalRef.current) { clearInterval(pingIntervalRef.current); pingIntervalRef.current = null; }
      if (!cleaningUpRef.current) scheduleReconnect();
    };
    wsRef.current = websocket;
  };

  const scheduleReconnect = () => {
    reconnectAttemptRef.current += 1;
    const delay = reconnectAttemptRef.current === 1 ? 0 : Math.min(1000 * Math.pow(2, reconnectAttemptRef.current - 2), 10000);
    reconnectTimeoutRef.current = setTimeout(() => { if (connectWebSocketRef.current) connectWebSocketRef.current(); }, delay);
  };

  useEffect(() => {
    if (connectWebSocketRef.current) connectWebSocketRef.current();
    const handleVis = () => {
      if (document.visibilityState === 'visible' && (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN)) {
        reconnectAttemptRef.current = 0;
        if (connectWebSocketRef.current) connectWebSocketRef.current();
      }
    };
    const handleOnline = () => { reconnectAttemptRef.current = 0; pollFailCount.current = 0; if (connectWebSocketRef.current) connectWebSocketRef.current(); };
    document.addEventListener('visibilitychange', handleVis);
    window.addEventListener('online', handleOnline);
    return () => {
      cleaningUpRef.current = true;
      document.removeEventListener('visibilitychange', handleVis);
      window.removeEventListener('online', handleOnline);
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
      if (pingIntervalRef.current) clearInterval(pingIntervalRef.current);
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      if (wsRef.current) { try { wsRef.current.close(); } catch (e) { /* ignore */ } }
    };
  }, [locationId]);

  // ─── Sustained disconnect banner (60s) ──────────────────────────────
  useEffect(() => {
    let timer;
    if (connectionStatus !== 'connected') {
      timer = setTimeout(() => setShowDisconnectBanner(true), 60000);
    } else { setShowDisconnectBanner(false); }
    return () => clearTimeout(timer);
  }, [connectionStatus]);

  // ─── Helpers ─────────────────────────────────────────────────────────
  const getDogNames = (notif) => {
    if (notif.animals && notif.animals.length > 0) return notif.animals.map(a => a.animal_name);
    return [notif.animal_name];
  };
  const getBreeds = (notif) => {
    if (notif.animals && notif.animals.length > 0) return notif.animals.map(a => a.breed).filter(Boolean);
    return notif.breed ? [notif.breed] : [];
  };
  const isCustomMessage = (notif) => notif?.type === 'custom_message';

  // Dogs filtered for this room (for idle display)
  const roomDogs = goingHomeDogs;
  const spotlightDog = roomDogs.length > 0 ? roomDogs[spotlightIndex % roomDogs.length] : null;
  
  // Next pickup: sort by checkout_time, get first 2
  const nextPickups = [...roomDogs]
    .filter(d => d.checkout_time)
    .sort((a, b) => (a.checkout_time || '').localeCompare(b.checkout_time || ''))
    .slice(0, 2);

  if (!BACKEND_URL && !window.location.host) {
    return (
      <div className="min-h-screen bg-red-900 flex items-center justify-center p-4">
        <div className="text-center text-white">
          <h1 className="text-2xl font-bold mb-4">Configuration Error</h1>
          <p>Backend URL not configured</p>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  // FULL-SCREEN ALERT TAKEOVER (when a dog is going home)
  // ═══════════════════════════════════════════════════════════════════════
  if (alerts.active.length > 1) {
    return <GoingHomeAlertGrid alerts={alerts.active} queued={alerts.queue.length} bg={roomColors.bg} />;
  }
  if (currentNotification) {
    return (
      <div className={`min-h-screen min-h-[100dvh] bg-gradient-to-br ${roomColors.bg} overflow-hidden`}>
        {/* Disconnect banner */}
        {showDisconnectBanner && (
          <div className="fixed top-0 left-0 right-0 bg-red-600 text-white py-3 px-6 z-50 animate-pulse">
            <div className="flex items-center justify-center gap-4 text-xl font-bold">
              <AlertTriangle className="h-7 w-7" />
              <span>NOT CONNECTED TO SERVER</span>
              <AlertTriangle className="h-7 w-7" />
            </div>
          </div>
        )}

        {/* Custom Message Alert */}
        {isCustomMessage(currentNotification) && (
          <div className="flex items-center justify-center min-h-screen p-8">
            <Card className="bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 border-4 border-emerald-300 shadow-2xl max-w-4xl w-full">
              <div className="p-8 md:p-12">
                <div className="text-center">
                  <div className="text-5xl md:text-6xl mb-4">
                    <MessageSquare className="h-16 w-16 mx-auto text-white" />
                  </div>
                  <h2 className="text-3xl md:text-5xl font-bold text-white mb-6" style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.3)' }}>
                    MESSAGE FROM FRONT DESK
                  </h2>
                  <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-6 md:p-8 mb-6">
                    <p className="text-2xl md:text-5xl font-bold text-white leading-relaxed" style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.2)' }}>
                      {currentNotification.message}
                    </p>
                  </div>
                  <div className="flex flex-wrap justify-center gap-4 text-white text-xl">
                    <div className="flex items-center gap-3 bg-white/20 backdrop-blur-sm px-6 py-3 rounded-xl">
                      <User className="h-6 w-6" />
                      <span className="font-semibold">From: {currentNotification.sender_name}</span>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Dog Pickup Alert — FULL SCREEN */}
        {!isCustomMessage(currentNotification) && (
          <div className="flex items-center justify-center min-h-screen p-6 animate-bounce">
            <Card className="bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 border-4 border-yellow-300 shadow-2xl w-full max-w-7xl overflow-hidden">
              <div className="p-6 md:p-8">
                {/* Alert Header */}
                <div className="flex items-center justify-center gap-4 mb-6">
                  <span className="text-5xl md:text-6xl">&#x1F6A8;</span>
                  <h3 className="text-4xl md:text-5xl font-black text-white uppercase tracking-wider" style={{ textShadow: '3px 3px 6px rgba(0,0,0,0.3)' }}>
                    Going Home!
                  </h3>
                  <span className="text-5xl md:text-6xl">&#x1F6A8;</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                  {/* LEFT — Dog Images */}
                  <div className="flex items-center justify-center">
                    {(!currentNotification.animals || currentNotification.animals.length <= 1) && (
                      <DogImage
                        url={currentNotification.image_url || currentNotification.animals?.[0]?.image_url}
                        name={currentNotification.animal_name || currentNotification.animals?.[0]?.animal_name}
                        size="xl"
                      />
                    )}
                    {currentNotification.animals && currentNotification.animals.length === 2 && (
                      <div className="flex gap-4 md:gap-6">
                        {currentNotification.animals.map((animal, idx) => (
                          <div key={idx} className="flex flex-col items-center">
                            <DogImage url={animal.image_url} name={animal.animal_name} size="lg" />
                            <p className="text-2xl md:text-3xl font-black text-white mt-3 text-center" style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.3)' }}>
                              {animal.animal_name}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                    {currentNotification.animals && currentNotification.animals.length >= 3 && (
                      <div className="grid grid-cols-2 gap-3 md:gap-4">
                        {currentNotification.animals.slice(0, 4).map((animal, idx) => (
                          <div key={idx} className="flex flex-col items-center">
                            <DogImage url={animal.image_url} name={animal.animal_name} size="md" />
                            <p className="text-xl md:text-2xl font-bold text-white mt-2 text-center">{animal.animal_name}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {/* RIGHT — Names and Details */}
                  <div className="flex flex-col justify-center items-center md:items-start text-center md:text-left">
                    <h2 className="text-5xl md:text-7xl lg:text-8xl font-black text-white mb-4 leading-tight" style={{ textShadow: '4px 4px 8px rgba(0,0,0,0.4)' }}>
                      {getDogNames(currentNotification).join(" & ")}
                    </h2>
                    {getBreeds(currentNotification).length > 0 && (
                      <div className="bg-white/25 backdrop-blur-sm rounded-2xl px-6 py-3 mb-6">
                        <p className="text-2xl md:text-3xl lg:text-4xl font-semibold text-white">
                          {getBreeds(currentNotification).join(" | ")}
                        </p>
                      </div>
                    )}
                    <div className="space-y-4 w-full">
                      <div className="flex items-center gap-4 bg-white/20 backdrop-blur-sm px-6 py-4 rounded-2xl">
                        <User className="h-10 w-10 md:h-12 md:w-12 text-white" />
                        <div>
                          <p className="text-lg text-white/80">Owner</p>
                          <p className="text-3xl md:text-4xl font-bold text-white">{currentNotification.owner_name}</p>
                        </div>
                      </div>
                      {currentNotification.checkout_time && (
                        <div className="flex items-center gap-4 bg-white/20 backdrop-blur-sm px-6 py-4 rounded-2xl">
                          <Clock className="h-10 w-10 md:h-12 md:w-12 text-white" />
                          <div>
                            <p className="text-lg text-white/80">Pickup Time</p>
                            <p className="text-3xl md:text-4xl font-bold text-white">{currentNotification.checkout_time}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        )}
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  // IDLE STATE — SPLIT SCREEN LAYOUT
  // Left: Going Home list | Right: Rotating panels
  // ═══════════════════════════════════════════════════════════════════════
  return (
    <div className={`min-h-screen min-h-[100dvh] bg-gradient-to-br ${roomColors.bg} p-4 overflow-hidden flex flex-col`} data-testid="tv-display-idle">
      {/* Disconnect Banner */}
      {showDisconnectBanner && (
        <div className="fixed top-0 left-0 right-0 bg-red-600 text-white py-3 px-6 z-50 animate-pulse">
          <div className="flex items-center justify-center gap-4 text-lg font-bold">
            <AlertTriangle className="h-6 w-6" />
            <span>NOT CONNECTED TO SERVER - NOTIFICATIONS WILL NOT APPEAR</span>
            <AlertTriangle className="h-6 w-6" />
          </div>
        </div>
      )}

      {/* ── Top Bar ──────────────────────────────────────────────────── */}
      <div className={`flex justify-between items-center mb-3 ${showDisconnectBanner ? 'mt-16' : ''}`}>
        {/* Left: Clock */}
        <div className="bg-black/30 backdrop-blur-sm rounded-xl px-5 py-3">
          <div className="flex items-center gap-3">
            <Clock className="h-7 w-7 text-white/80" />
            <div>
              <div className="text-4xl font-bold text-white tabular-nums">
                {currentTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
              </div>
              <div className="text-sm text-white/60">
                {currentTime.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
              </div>
            </div>
          </div>
        </div>

        {/* Center: Room label + Location + Status */}
        <div className="flex flex-col items-center gap-1">
          <h1 className="text-3xl font-bold text-white flex items-center gap-3" style={{ fontFamily: 'Manrope' }}>
            <Home className="h-8 w-8" />
            {roomParam ? getRoomLabel(roomParam) : 'Daycare Pickup Board'}
          </h1>
          <div className="flex items-center gap-3">
            {locationName && (
              <div className="flex items-center gap-1.5">
                <MapPin className="h-4 w-4 text-white/60" />
                <span className="text-sm text-white/60">{locationName}</span>
              </div>
            )}
            <Badge className={`text-white text-sm px-3 py-1 ${connectionStatus === 'connected' ? 'bg-green-500' : 'bg-red-500'}`} data-testid="connection-status-badge">
              <span className="animate-pulse mr-1">&#x25CF;</span>
              {connectionStatus === 'connected' ? 'LIVE' : 'OFFLINE'}
            </Badge>
          </div>
        </div>

        {/* Right: Weather + Sound */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => { setSoundEnabled(!soundEnabled); if (!soundEnabled) playDogFriendlyNotificationSound(); }}
            className={`flex items-center gap-1 px-3 py-2 rounded-full transition-all text-sm ${soundEnabled ? 'bg-purple-500/80 text-white' : 'bg-gray-600/50 text-gray-400'}`}
          >
            <Volume2 className="h-4 w-4" />
          </button>
          {weather && (
            <div className="bg-black/30 backdrop-blur-sm rounded-xl px-5 py-3">
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <div className="text-3xl font-bold text-white">{weather.temperature}&#xB0;F</div>
                  <div className="text-sm text-white/60">{getWeatherDescription(weather.weatherCode)}</div>
                </div>
                <Thermometer className="h-7 w-7 text-white/80" />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Main Split Layout ──────────────────────────────────────── */}
      <div className="flex-1 grid grid-cols-5 gap-4 min-h-0">
        
        {/* LEFT PANEL (3/5) — Going Home List */}
        <div className="col-span-3 flex flex-col min-h-0">
          <div className="flex items-center gap-3 mb-3">
            <DogIcon className="h-7 w-7 text-white/70" />
            <h2 className="text-2xl font-semibold text-white/90">
              Going Home Today
              {roomDogs.length > 0 && <span className="ml-2 text-white/50 font-normal">({roomDogs.length})</span>}
            </h2>
          </div>

          {roomDogs.length === 0 ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <DogIcon className="h-24 w-24 text-white/20 mx-auto mb-4 animate-pulse" />
                <p className="text-3xl text-white/40 font-medium">No dogs going home yet</p>
                <p className="text-lg text-white/25 mt-2">List updates automatically</p>
              </div>
            </div>
          ) : (
            <div className="flex-1 overflow-hidden">
              <div className="grid grid-cols-2 gap-3 auto-rows-min max-h-full overflow-y-auto pr-1" style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(255,255,255,0.15) transparent' }}>
                {roomDogs.map((dog, idx) => (
                  <div
                    key={dog.animal_id || idx}
                    className="bg-white/8 backdrop-blur-sm rounded-xl p-4 flex items-center gap-4 border border-white/10 hover:bg-white/12 transition-colors"
                    data-testid={`going-home-dog-${dog.animal_id}`}
                  >
                    {/* Dog photo */}
                    {dog.image_url ? (
                      <div className="w-20 h-20 rounded-xl overflow-hidden border-2 border-white/20 flex-shrink-0">
                        <img src={dog.image_url} alt={dog.animal_name} className="w-full h-full object-cover" onError={(e) => { e.target.style.display='none'; e.target.parentElement.innerHTML='<div class="w-full h-full bg-white/10 flex items-center justify-center"><svg class="w-8 h-8 text-white/40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/></svg></div>'; }} />
                      </div>
                    ) : (
                      <div className="w-20 h-20 rounded-xl bg-white/10 flex items-center justify-center border-2 border-white/15 flex-shrink-0">
                        <DogIcon className="w-10 h-10 text-white/40" />
                      </div>
                    )}
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-xl font-bold text-white truncate">{dog.animal_name}</p>
                        {dog.room && (
                          <span className={`text-xs font-bold px-2 py-0.5 rounded ${dog.room === 'large' ? 'bg-emerald-500/30 text-emerald-300' : 'bg-blue-500/30 text-blue-300'}`}>
                            {dog.room === 'large' ? 'L' : 'S'}
                          </span>
                        )}
                      </div>
                      <p className="text-base text-white/50 truncate">{dog.breed}</p>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-base text-white/40">{dog.owner_name}</span>
                        {dog.checkout_time && (
                          <span className="text-lg text-yellow-300/80 font-semibold">{dog.checkout_time}</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* RIGHT PANEL (2/5) — Alerts + Next Pickup + Spotlight */}
        <div className="col-span-2 flex flex-col gap-3 min-h-0">
          
          {/* Recent Alerts — BIG text for TV viewing */}
          {allNotifications.length > 0 && (
            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-5 border border-white/10">
              <h3 className="text-xl font-bold text-white/70 uppercase tracking-wider mb-4">Recent Alerts</h3>
              <div className="space-y-3">
                {allNotifications.slice(0, 3).map((notif, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <span className="text-yellow-400 text-2xl mt-0.5">&#x25CF;</span>
                    <span className="text-2xl text-white font-semibold leading-snug">
                      {isCustomMessage(notif) ? notif.message?.slice(0, 60) : getDogNames(notif).join(' & ')}
                    </span>
                    <span className="text-white/40 text-lg ml-auto flex-shrink-0 mt-1">
                      {new Date(notif.timestamp).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Next Pickup — always visible */}
          <div className="bg-white/5 backdrop-blur-sm rounded-xl p-5 border border-white/10 flex-1 min-h-0 flex flex-col" data-testid="next-pickup-panel">
            <h3 className="text-xl font-bold text-white/70 uppercase tracking-wider mb-4 flex items-center gap-2">
              <Clock className="h-6 w-6" />
              Next Pickup
            </h3>
            {nextPickups.length > 0 ? (
              <div className="flex-1 flex flex-col justify-start gap-4 overflow-y-auto">
                {nextPickups.map((dog, idx) => (
                  <div key={idx} className="bg-white/8 rounded-xl p-5 border border-white/10">
                    <div className="flex items-center gap-5">
                      {dog.image_url ? (
                        <div className="w-24 h-24 rounded-xl overflow-hidden border-2 border-white/20 flex-shrink-0">
                          <img src={dog.image_url} alt={dog.animal_name} className="w-full h-full object-cover" onError={(e) => { e.target.style.display='none'; }} />
                        </div>
                      ) : (
                        <div className="w-24 h-24 rounded-xl bg-white/10 flex items-center justify-center border-2 border-white/15 flex-shrink-0">
                          <DogIcon className="w-12 h-12 text-white/30" />
                        </div>
                      )}
                      <div className="flex-1">
                        <p className="text-3xl font-bold text-white">{dog.animal_name}</p>
                        <p className="text-lg text-white/50">{dog.breed}</p>
                        <div className="flex items-center gap-2 mt-1.5">
                          <User className="h-4 w-4 text-white/40" />
                          <span className="text-lg text-white/60">{dog.owner_name}</span>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-4xl font-bold text-yellow-300">{dog.checkout_time}</p>
                        <p className="text-sm text-white/40">pickup</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <p className="text-white/30 text-xl">No pickup times available</p>
              </div>
            )}
          </div>

          {/* Dog Spotlight — compact bottom corner with big photo */}
          {spotlightDog && (
            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10 flex items-center gap-4" data-testid="dog-spotlight-panel">
              {spotlightDog.image_url ? (
                <div className="w-28 h-28 rounded-xl overflow-hidden border-2 border-white/20 flex-shrink-0">
                  <img src={spotlightDog.image_url} alt={spotlightDog.animal_name} className="w-full h-full object-cover" onError={(e) => { e.target.style.display='none'; }} />
                </div>
              ) : (
                <div className="w-28 h-28 rounded-xl bg-white/10 flex items-center justify-center border-2 border-white/15 flex-shrink-0">
                  <DogIcon className="w-14 h-14 text-white/20" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <h4 className="text-2xl font-black text-white truncate">{spotlightDog.animal_name}</h4>
                {spotlightDog.breed && <p className="text-lg text-white/50 truncate">{spotlightDog.breed}</p>}
                {spotlightDog.owner_name && <p className="text-base text-white/40 truncate">{spotlightDog.owner_name}</p>}
                {spotlightDog.checkout_time && <p className="text-xl text-yellow-300/80 font-semibold">{spotlightDog.checkout_time}</p>}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer — K9 branding */}
      <div className="flex justify-between items-center mt-2 text-white/40 text-sm">
        <p>{locationName ? `K9 Resorts of ${locationName.replace('K9 Resorts ', '').replace('K9 Resorts', '')}` : 'K9 Resorts'}</p>
        {roomParam && <p className={`font-medium ${roomColors.text}`}>{getRoomLabel(roomParam)}</p>}
      </div>

      {/* Anti-sleep element */}
      <div className="fixed bottom-0 left-0 w-1 h-1 opacity-0 pointer-events-none" style={{ animation: 'pulse 10s infinite' }} />
      <style>{`
        @keyframes pulse { 0%, 100% { transform: translateX(0); } 50% { transform: translateX(1px); } }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fadeIn { animation: fadeIn 0.4s ease-out; }
      `}</style>
    </div>
  );
}

// ─── Reusable Dog Image Component ──────────────────────────────────────
function DogImage({ url, name, size = 'xl' }) {
  const sizeClasses = {
    xl: 'w-72 h-72 md:w-96 md:h-96 lg:w-[450px] lg:h-[450px] rounded-3xl border-8',
    lg: 'w-52 h-52 md:w-72 md:h-72 lg:w-80 lg:h-80 rounded-2xl border-6',
    md: 'w-40 h-40 md:w-52 md:h-52 lg:w-60 lg:h-60 rounded-xl border-4',
  };
  const iconSizes = { xl: 'w-40 h-40 md:w-48 md:h-48', lg: 'w-24 h-24 md:w-32 md:h-32', md: 'w-16 h-16 md:w-20 md:h-20' };

  if (url) {
    return (
      <div className={`${sizeClasses[size]} overflow-hidden border-white shadow-2xl`}>
        <img src={url} alt={name} className="w-full h-full object-cover"
          onError={(e) => { e.target.parentElement.innerHTML = `<div class="w-full h-full bg-white/30 flex items-center justify-center"><svg class="${iconSizes[size]} text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/><line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/></svg></div>`; }}
        />
      </div>
    );
  }
  return (
    <div className={`${sizeClasses[size]} bg-white/30 flex items-center justify-center border-white/50`}>
      <DogIcon className={`${iconSizes[size]} text-white`} />
    </div>
  );
}

export default GoingHomeDisplay;
